/**
 * `scribeDraftPayload.ts` — FE reader for the LOSSLESS scribe draft blob
 * persisted on a Progress Note `DocumentReference` (preliminary, and retained
 * on the final note after Lock).
 *
 * Mirror of the middleware writer in
 * `packages/middleware/src/visit/visit.cda.ts`
 * (`buildScribeDraftPayloadExtension` / `SCRIBE_DRAFT_PAYLOAD_EXTENSION_URL`).
 *
 * Why this exists: the clinical cascade is deferred to Lock. At every auto-save
 * the middleware stores the EXACT `{ soapNote, context }` inputs the cascade
 * will replay at Lock as a single JSON `valueString` extension on the
 * preliminary DocRef. Lock reads it back server-side.
 *
 * The edit form re-open path used to re-hydrate medications from the
 * `QuestionnaireResponse` (`hydrateMedicationsFromQr`), which is LOSSY — it
 * hard-codes `dose:null, route:null, frequency:null, status:'active'`. Under
 * gate-until-lock there are NO chart MedicationStatements for the encounter
 * pre-lock, so re-opening a preliminary note dropped every med's
 * dose / route / frequency / status. This reader lets the edit form re-hydrate
 * meds from the SAME lossless blob the Lock step trusts, so dosing + status
 * survive edit → re-open → save → lock.
 *
 * The lock handler KEEPS this extension when it flips the DocRef to `final`
 * (only unselected billing candidates are pruned), so the locked-note viewer
 * re-renders the full structured detail from the same lossless blob the edit
 * form re-opens.
 */
import type { DocumentReference } from '@medplum/fhirtypes'
import { lowConfidenceKey } from '@behuman/core-contract'
import { formatRosNarrative, type SoapNoteDto } from './draftToSoap'
import { logWarning } from '@/utils/errorUtils'
import { QR_VITAL_LINKIDS } from './progressNoteQrHydrate'
import { VITAL_UNIT_FIELDS, resolveSavedVitalUnit } from './vitalUnits'
import type {
  AllergyItem,
  BillingEntry,
  ConditionItem,
  FamilyHistoryItem,
  FollowUpItem,
  MedicationItem,
  ObservationItem,
  ProcedureItem,
  RosEntry,
  ServiceRequestItem,
  TimeEntryItem,
} from './progressNoteEditSchema'

/** Must match `SCRIBE_DRAFT_PAYLOAD_EXTENSION_URL` in `visit.cda.ts`. */
export const SCRIBE_DRAFT_PAYLOAD_EXTENSION_URL =
  'https://behuman.health/fhir/StructureDefinition/scribe-draft-payload'

/** Shape of the parsed `valueString` JSON. `context` is opaque to the FE. */
export interface ScribeDraftPayload {
  soapNote: SoapNoteDto
  context: unknown
}

/**
 * Parse the lossless `{ soapNote, context }` blob off a Progress Note
 * `DocumentReference` (preliminary or final — the blob is retained at Lock).
 * Returns `undefined` when the extension is absent (legacy / non-scribe note
 * that never had a blob) or unparseable — callers fall back to the lossy QR
 * hydrate.
 */
export function readScribeDraftPayload(
  documentReference: DocumentReference | undefined,
): ScribeDraftPayload | undefined {
  const ext = documentReference?.extension?.find(
    (e) => e.url === SCRIBE_DRAFT_PAYLOAD_EXTENSION_URL,
  )
  const raw = ext?.valueString
  if (!raw) return undefined
  try {
    const parsed = JSON.parse(raw) as Partial<ScribeDraftPayload>
    if (!parsed || typeof parsed !== 'object' || !parsed.soapNote) return undefined
    return { soapNote: parsed.soapNote, context: parsed.context }
  } catch (err) {
    // Non-fatal — the caller falls back to QR hydrate. Surface for triage.
    logWarning(
      'progress-note-edit',
      `Unparseable scribe-draft-payload on DocumentReference/${documentReference?.id}`,
      err instanceof Error ? err.message : String(err),
    )
    return undefined
  }
}

/**
 * Reconstruct the form's `medications[]` array from the lossless
 * `SoapNoteDto`, preserving dose / route / frequency / note AND the
 * doctor's medication status — the data the QR hydrate drops.
 *
 * The blob splits meds the same way `draftToSoap` writes them:
 *   - `treatmentsPrescribed[]` → new prescriptions.
 *   - `medsReviewed[]` → continuations + discontinuations.
 * Both carry an explicit `medicationStatus` (absent only in legacy blobs →
 * default `active`).
 *
 * We invert that back into the form's `{ intent, status }` pair:
 *   - prescribed → intent `new-prescription`, status from `medicationStatus`
 *   - reviewed   → intent from stopped/not, status from `medicationStatus`
 *
 * `medicationStatus` carries the full FHIR set, so the doctor's chosen status
 * (completed / intended / on-hold / …) round-trips through a save.
 */
export function hydrateMedicationsFromScribeDraft(soapNote: SoapNoteDto): MedicationItem[] {
  const flagged = lowConfidenceFlags(soapNote)
  const prescribed: MedicationItem[] = (soapNote.treatmentsPrescribed ?? []).map((t) => ({
    id: crypto.randomUUID(),
    code: t.code ?? '',
    display: t.display ?? t.code ?? '',
    status: t.medicationStatus ?? 'active',
    intent: 'new-prescription',
    dose: t.dose ?? null,
    route: t.route ?? null,
    frequency: t.frequency ?? null,
    note: t.note ?? null,
    ...flagged('medication', t.code, t.display),
  }))

  const reviewed: MedicationItem[] = (soapNote.medsReviewed ?? []).map((t) => ({
    id: crypto.randomUUID(),
    code: t.code ?? '',
    display: t.display ?? t.code ?? '',
    status: t.medicationStatus ?? 'active',
    intent: t.medicationStatus === 'stopped' ? 'discontinuation' : 'continuation',
    dose: t.dose ?? null,
    route: t.route ?? null,
    frequency: t.frequency ?? null,
    note: t.note ?? null,
    ...flagged('medication', t.code, t.display),
  }))

  return [...reviewed, ...prescribed]
}

/**
 * Reconstruct the form's `procedures[]` array from the lossless `SoapNoteDto`'s
 * `proceduresAdministered[]` (CPT codings). The wire carries only the coding
 * (system/code/display) — `status` and `performedDateTime` aren't part of
 * `SoapNoteDto`, so we apply the form schema defaults (`status: 'completed'`,
 * `performedDateTime: null`), matching the QR hydrator. The point of going
 * through the blob (vs the QR) here is consistency: edit re-open hydrates EVERY
 * structured array from the same lossless source Lock trusts, so procedures
 * survive edit → re-open → save → lock instead of defaulting to `[]` and being
 * wiped on the next save (root cause C4).
 */
export function hydrateProceduresFromScribeDraft(soapNote: SoapNoteDto): ProcedureItem[] {
  return (soapNote.proceduresAdministered ?? []).map((p) => ({
    id: crypto.randomUUID(),
    code: p.code ?? '',
    display: p.display ?? p.code ?? '',
    status: 'completed',
    performedDateTime: null,
  }))
}

/**
 * Reconstruct the form's `conditions[]` from the lossless `SoapNoteDto`,
 * preserving `clinicalStatus` (Status pill / chart Diagnoses tab) and
 * `onsetDateTime` (chart Diagnoses "Started" column) — the fields the lossy QR
 * hydrate hard-codes to `active` / `null`. Encounter diagnoses (`assessment1`)
 * come first with sequential `rank`, then problem-list items
 * (`pastMedicalHistory`). `severity` is not persisted (no chart column renders
 * it), so it defaults to `null`.
 */
export function hydrateConditionsFromScribeDraft(soapNote: SoapNoteDto): ConditionItem[] {
  const encounterDx: ConditionItem[] = (soapNote.assessment1 ?? []).map((c, idx) => ({
    id: crypto.randomUUID(),
    code: c.code ?? '',
    display: c.display ?? c.code ?? '',
    category: 'encounter-diagnosis',
    clinicalStatus: c.clinicalStatus ?? 'active',
    onsetDateTime: c.onsetDateTime ?? null,
    severity: null,
    rank: idx + 1,
    note: '',
    external: c.external,
  }))
  const problemList: ConditionItem[] = (soapNote.pastMedicalHistory ?? []).map((c) => ({
    id: crypto.randomUUID(),
    code: c.code ?? '',
    display: c.display ?? c.code ?? '',
    category: 'problem-list-item',
    clinicalStatus: c.clinicalStatus ?? 'active',
    onsetDateTime: c.onsetDateTime ?? null,
    severity: null,
    rank: null,
    note: '',
    // Restore the chart-seeded flag; pre-flag blobs hydrate `undefined` and
    // are backfilled from the live chart on reopen.
    external: c.external,
  }))
  return [...encounterDx, ...problemList]
}

/**
 * Reconstruct the form's `allergies[]` from the lossless `SoapNoteDto`,
 * preserving `clinicalStatus` + `reaction` (the chart Allergies tab's Status +
 * Reaction columns) that the QR hydrate hard-codes to `active` / `null`.
 * PD-1141 follow-up: `severity` round-trips through the blob like the rest.
 */
export function hydrateAllergiesFromScribeDraft(soapNote: SoapNoteDto): AllergyItem[] {
  const flagged = lowConfidenceFlags(soapNote)
  return (soapNote.allergies ?? []).map((a) => ({
    id: crypto.randomUUID(),
    system: a.system ?? null,
    code: a.code ?? null,
    display: a.display ?? '',
    clinicalStatus: a.clinicalStatus ?? 'active',
    reaction: a.reaction ?? null,
    severity: a.severity ?? null,
    category: a.category ?? null,
    external: a.external,
    // PD-1141 — restore the chart-row pointer + the "Updated" marker.
    chartId: a.chartId ?? null,
    updated: a.updated,
    ...flagged('allergy', a.code, a.display),
  }))
}

/** PD-1600 — what a reopened low-confidence row says; the AI's own reason does not survive the blob. */
export const LOW_CONFIDENCE_REASON =
  'The AI was not confident it heard or translated this correctly — confirm or correct before locking.'

/** The rows `soapNote.lowConfidenceFields` still flags, as the form fields to spread onto a row. */
function lowConfidenceFlags(soapNote: SoapNoteDto) {
  const keys = new Set(soapNote.lowConfidenceFields ?? [])
  return (
    kind: 'medication' | 'allergy' | 'observation',
    code: string | null | undefined,
    display: string | null | undefined,
  ): { needsReview?: true; reviewReason?: string } =>
    keys.size && keys.has(lowConfidenceKey(kind, code, display))
      ? { needsReview: true, reviewReason: LOW_CONFIDENCE_REASON }
      : {}
}

/**
 * The flagged keys the editor can actually surface as a confirmable row
 * (PD-1600): the two medication lists, allergies, and only those typed vitals
 * that carry a value — the draft writer flags any uncertain observation, but an
 * exam finding, a lab, or a vital with no reading never hydrates into a row, so
 * its key could never be cleared and Lock stayed disabled forever. Filtered on
 * read so notes already saved with such a key heal too.
 */
export function resolvableLowConfidenceKeys(soapNote: SoapNoteDto): string[] {
  const fields = soapNote as Record<string, unknown>
  const hasValue = (linkId: string) => {
    const raw = fields[linkId]
    return typeof raw === 'string' && raw.trim().length > 0
  }
  const resolvable = new Set<string>([
    ...QR_VITAL_LINKIDS.filter((v) => hasValue(v.linkId)).map((v) =>
      lowConfidenceKey('observation', v.loinc, v.display),
    ),
    ...[...(soapNote.treatmentsPrescribed ?? []), ...(soapNote.medsReviewed ?? [])].map((t) =>
      lowConfidenceKey('medication', t.code, t.display),
    ),
    ...(soapNote.allergies ?? []).map((a) => lowConfidenceKey('allergy', a.code, a.display)),
  ])
  return (soapNote.lowConfidenceFields ?? []).filter((key) => resolvable.has(key))
}

/**
 * Reconstruct the form's `serviceRequests[]` (Orders) from the lossless
 * `SoapNoteDto`, preserving `intent` (Orders status pill) + `priority` (Orders
 * priority pill) + `category` + `reasonText` that the QR hydrate hard-codes /
 * infers. Falls back to the cascade defaults when a field is absent.
 */
export function hydrateServiceRequestsFromScribeDraft(soapNote: SoapNoteDto): ServiceRequestItem[] {
  return (soapNote.testsOrdered ?? []).map((s) => ({
    id: crypto.randomUUID(),
    system: s.system ?? '',
    code: s.code ?? '',
    display: s.display ?? '',
    category: s.category ?? 'laboratory',
    intent: s.intent ?? 'order',
    priority: s.priority ?? 'routine',
    reasonText: s.reasonText ?? null,
  }))
}

/**
 * Read `summary.ros` from the blob's `reviewOfSystemsStructured` slot; legacy → `[]`.
 * PD-1804: rows only when `objective` still opens with their ROS block (the exam
 * strip removed it). Otherwise the prose was edited after the rows were written,
 * so they may be stale and would repeat a finding the examination already shows.
 */
export function hydrateRosFromScribeDraft(soapNote: SoapNoteDto): RosEntry[] {
  const objective = soapNote.objective ?? ''
  return stripRosBlock(soapNote) !== objective ? (soapNote.reviewOfSystemsStructured ?? []) : []
}

/**
 * The edit form's hydrate-time `existing` snapshot for `draftToSoap`: the
 * narratives, plus (PD-1804) the stored ROS rows `hydrateRosFromScribeDraft`
 * hides, so a save that adds no ROS rows keeps them. QR-only notes carry none.
 */
export function buildExistingSoapSnapshot(
  narratives: Partial<SoapNoteDto>,
  soapNote: SoapNoteDto | undefined,
): Partial<SoapNoteDto> {
  const hiddenRos =
    soapNote && !hydrateRosFromScribeDraft(soapNote).length
      ? (soapNote.reviewOfSystemsStructured ?? [])
      : []
  return { ...narratives, reviewOfSystemsStructured: hiddenRos }
}

/**
 * The snapshot for the next save, once `saved` is built from `snapshot`. PD-1804:
 * rows the form supplied replace the carried ones, so carry none from then on.
 * Otherwise `draftToSoap` reused the carried array itself; keep carrying it, even
 * when that save makes the rows visible, because the form still does not hold them.
 */
export function refreshExistingSoapSnapshot(
  snapshot: Partial<SoapNoteDto>,
  saved: SoapNoteDto,
): Partial<SoapNoteDto> {
  return saved.reviewOfSystemsStructured === snapshot.reviewOfSystemsStructured
    ? snapshot
    : { ...snapshot, reviewOfSystemsStructured: [] }
}

/** Chart-derived Family History rows from the blob's structured slot (PD-1377); legacy → `[]`. */
export function hydrateFamilyHistoryFromScribeDraft(soapNote: SoapNoteDto): FamilyHistoryItem[] {
  return (soapNote.familyHistoryStructured ?? []).map((entry) => ({
    id: crypto.randomUUID(),
    relative: entry.relative,
    conditions: entry.conditions,
    deceased: entry.deceased,
  }))
}

/**
 * Blob-first analogue for the billing-modal entries. `billingEntries` otherwise
 * hydrates only from a materialized `Claim`, which a preliminary note doesn't
 * have (gate-until-lock) — so a saved Amt/entry was dropped on every re-hydrate.
 */
export function hydrateBillingEntriesFromScribeDraft(soapNote: SoapNoteDto): BillingEntry[] {
  return (soapNote.billing ?? []).map((entry) => ({
    id: crypto.randomUUID(),
    procedure: entry.procedure?.code ? entry.procedure : undefined,
    price: entry.price,
    quantity: entry.quantity ?? 1,
    billingItems: (entry.billingItems ?? []).map((item) => ({
      id: crypto.randomUUID(),
      dx: item.dx?.code ? item.dx : undefined,
    })),
    modifiers: entry.modifiers ?? [],
  }))
}

/**
 * The doctor's examination prose: `objective` minus the ROS flatten that
 * `draftToSoap` prepends. Stripping it here keeps ROS single-sourced in
 * `summary.ros` — hydrating both would re-fold ROS into `objective` on every
 * save. Legacy blobs without structured ROS return `objective` whole.
 */
export function hydrateExamNarrativeFromScribeDraft(soapNote: SoapNoteDto): string {
  // Strip any legacy `Prior objective narrative:` prefix so old blobs render prose alone.
  return stripRosBlock(soapNote).replace(/^(Prior objective narrative:\s*)+/, '')
}

// PD-1631 boundary rule: strip the ROS block only when it is the whole objective
// or a blank line follows it, so prose that merely starts with the block's text
// is never cut mid-word. Returns `objective` unchanged when nothing was stripped.
function stripRosBlock(soapNote: SoapNoteDto): string {
  const objective = soapNote.objective ?? ''
  const rosBlock = formatRosNarrative(soapNote.reviewOfSystemsStructured ?? [])
  if (!rosBlock || !objective.startsWith(rosBlock)) return objective
  const rest = objective.slice(rosBlock.length)
  return !rest.trim() || rest.startsWith('\n\n') ? rest.replace(/^\n+/, '') : objective
}

/**
 * The trailing `exam` ObservationItem carrying the examination prose. Shared by
 * the edit form + locked viewer so they can't diverge. `display: ''` is
 * load-bearing — `draftToSoap` re-serializes a row verbatim (no label prefix)
 * only when it has neither code nor display, which is what stops the prose from
 * gaining a nested wrapper each save. Blank prose → no row.
 */
export function buildExamNarrativeRow(narrative: string): ObservationItem[] {
  if (!narrative.trim()) return []
  return [
    {
      id: crypto.randomUUID(),
      code: '',
      display: '',
      category: 'exam',
      valueQuantity: null,
      valueString: narrative,
      note: null,
    },
  ]
}

/** Split the legacy `prob` string into HPI paragraph rows (blank-line boundaries). */
export function splitHpiParagraphs(prob: string): { id: string; text: string }[] {
  return prob
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
    .map((text) => ({ id: crypto.randomUUID(), text }))
}

/** Parse the form's `followUps` from the blob's `followUp` string (split `\n`, strip `'• '`, drop blanks). */
export function hydrateFollowUpsFromScribeDraft(soapNote: SoapNoteDto): FollowUpItem[] {
  return (soapNote.followUp ?? '')
    .split('\n')
    .map((line) => line.replace(/^\s*•\s*/, '').trim())
    .filter((text) => text.length > 0)
    .map((text) => ({ id: crypto.randomUUID(), text }))
}

/** Blob-first analogue of `hydrateVitalObservationsFromQr` — maps the typed-vital fields to LOINC-coded rows via `QR_VITAL_LINKIDS`. */
export function hydrateVitalObservationsFromScribeDraft(soapNote: SoapNoteDto): ObservationItem[] {
  const fields = soapNote as Record<string, unknown>
  const flagged = new Set(soapNote.implausibleVitals ?? [])
  const lowConfidence = lowConfidenceFlags(soapNote)
  const out: ObservationItem[] = []
  for (const v of QR_VITAL_LINKIDS) {
    const raw = typeof fields[v.linkId] === 'string' ? (fields[v.linkId] as string) : ''
    const trimmed = raw.trim()
    if (!trimmed) continue
    const numeric = Number(trimmed)
    const note =
      v.noteLinkId && typeof fields[v.noteLinkId] === 'string'
        ? (fields[v.noteLinkId] as string).trim() || null
        : null
    const unitField = VITAL_UNIT_FIELDS[v.linkId]
    const rawUnit = unitField ? fields[unitField] : undefined
    const savedUnit = typeof rawUnit === 'string' ? rawUnit.trim() || undefined : undefined
    const unit = resolveSavedVitalUnit(v.loinc, savedUnit, v.unit)
    out.push({
      id: crypto.randomUUID(),
      code: v.loinc,
      display: v.display,
      category: 'vital-signs',
      valueQuantity: Number.isFinite(numeric) ? { value: numeric, unit } : null,
      valueString: Number.isFinite(numeric) ? null : trimmed,
      note,
      needsReview: flagged.has(v.loinc) || undefined,
      ...lowConfidence('observation', v.loinc, v.display),
    })
  }
  return out
}

/** Blob-first analogue of the QR `examReason`/`prob`/`objective`/`assessment`/`plan` reads — the SOAP narrative slots off the blob. */
export function hydrateSummaryFromScribeDraft(soapNote: SoapNoteDto): {
  examReason: string
  prob: string
  objective: string
  assessment: string
  plan: string
  mdm: string
} {
  return {
    examReason: soapNote.examReason ?? '',
    prob: soapNote.prob ?? '',
    objective: soapNote.objective ?? '',
    assessment: soapNote.assessment ?? '',
    plan: stripFollowUpBlockFromPlan(soapNote.plan ?? '', soapNote.followUp ?? ''),
    mdm: soapNote.mdm ?? '',
  }
}

/** Persisted `{label, minutes}` pairs → RHF rows (which need a stable `id`). */
export function toTimeEntryRows(
  entries: { label: string; minutes: number }[] | undefined,
): TimeEntryItem[] {
  return (entries ?? []).map((e) => ({
    id: crypto.randomUUID(),
    label: e.label,
    minutes: e.minutes,
  }))
}

/** Blob-first Total Time rows (PD-1321); `[]` for notes written before it. */
export function hydrateTimeEntriesFromScribeDraft(soapNote: SoapNoteDto): {
  providerTimeEntries: TimeEntryItem[]
  careCoordinatorTimeEntries: TimeEntryItem[]
} {
  const totalTime = soapNote.billingSuggestion?.totalTime
  return {
    providerTimeEntries: toTimeEntryRows(totalTime?.providerEntries),
    careCoordinatorTimeEntries: toTimeEntryRows(totalTime?.careCoordinatorEntries),
  }
}

/**
 * draftToSoap appends the bulleted follow-up block to `plan` as its own
 * `\n\n`-separated paragraph (`joinNonEmpty`); the form keeps follow-ups in
 * their own field, so hydrating the folded text back into `summary.plan` would
 * re-append it on every save. Strip ONLY the exact reconstructed fold as a
 * suffix — matching the provider's own trailing bullet list would drop real
 * plan content, so an inexact match (e.g. a legacy blob whose entry carried
 * inner newlines) is left intact rather than risk truncation.
 */
function stripFollowUpBlockFromPlan(plan: string, followUp: string): string {
  const block = followUp
    .split('\n')
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => `• ${t}`)
    .join('\n')
  if (!block || !plan.endsWith(block)) return plan
  return plan.slice(0, plan.length - block.length).trimEnd()
}
